import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { LemaRepite } from '#components/mobile/travesia/LemaRepite';
import { useTrato } from '#components/mobile/travesia/trato';
import { Boton } from '#components/mobile/ui/Boton';

import { Z_TRAVESIA } from './comun';

/** A los 3,4 s del paso 8 se abre la brújula con «Añadir movimiento». */
export const RETRASO_BRUJULA = 3400;

/** Hueco que se abre en Inicio para el bocadillo (como `arriba` en la maqueta). */
const ESTILO_HUECO =
  'body[data-travesia-hueco] [role="main"]{padding-top:calc(var(--travesia-hueco, 0px) + 14px)!important}';

type FinalInicioProps = {
  /** Nombre del perfil; sin él, Gavi no lo dice. */
  nombre: string;
  /** Llegó con «Saltar»: otro mensaje y sin lema. */
  saltado: boolean;
  ocupado?: boolean;
  /** Pasados 3,4 s: hay que abrir la brújula. */
  onAbrirBrujula: () => void;
  onEmpezar: () => void;
};

/**
 * Paso 8 · Inicio real con Gavi arriba: «Rumbo fijado, {n}. Ahora, repite
 * conmigo:», el lema frase a frase y «Empezar a navegar». A los 3,4 s pide
 * abrir la brújula (con «Añadir movimiento»). El bocadillo se coloca al
 * principio del contenido de Inicio y le abre hueco, como en la maqueta.
 */
export function FinalInicio({
  nombre,
  saltado,
  ocupado,
  onAbrirBrujula,
  onEmpezar,
}: FinalInicioProps) {
  const { t } = useTranslation();
  const { contexto } = useTrato();
  const panel = useRef<HTMLDivElement>(null);
  const [arriba, setArriba] = useState<number | null>(null);
  const [lemaVisto, setLemaVisto] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setLemaVisto(true);
      onAbrirBrujula();
    }, RETRASO_BRUJULA);
    return () => window.clearTimeout(id);
    // Una sola vez al llegar al paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se pone donde empieza el contenido de Inicio y le abre su hueco.
  useLayoutEffect(() => {
    const el = panel.current;
    if (!el) {
      return;
    }
    const colocar = () => {
      const main = document.querySelector('[role="main"]');
      const top = main?.getBoundingClientRect().top;
      setArriba(typeof top === 'number' && top > 0 ? top + 14 : null);
      document.body.style.setProperty(
        '--travesia-hueco',
        `${el.getBoundingClientRect().height}px`,
      );
    };
    document.body.dataset.travesiaHueco = '';
    colocar();
    const obs =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(colocar);
    obs?.observe(el);
    window.addEventListener('resize', colocar);
    return () => {
      obs?.disconnect();
      window.removeEventListener('resize', colocar);
      delete document.body.dataset.travesiaHueco;
      document.body.style.removeProperty('--travesia-hueco');
    };
  }, []);

  const texto = saltado
    ? t(
        'No problem: I will leave you some basic chests. Add an account whenever you like.',
      )
    : nombre.trim()
      ? t('Course set, {{name}}. Now, repeat after me:', {
          name: nombre.trim(),
          context: contexto,
        })
      : t('Course set. Now, repeat after me:', { context: contexto });

  return createPortal(
    <>
      <style>{ESTILO_HUECO}</style>
      <div
        ref={panel}
        data-testid="final-inicio"
        style={{
          position: 'fixed',
          left: 16,
          right: 16,
          top: arriba ?? 'calc(env(safe-area-inset-top, 0px) + 124px)',
          zIndex: Z_TRAVESIA.inicio,
        }}
      >
        <Bocadillo texto={texto} estado="vuela">
          {!saltado && (
            <LemaRepite
              quieto={lemaVisto}
              pie={t(
                'And whatever you spend, note it down with the compass: Add transaction.',
              )}
            />
          )}
          <div className="acciones">
            <Boton
              bloque
              isDisabled={ocupado}
              data-testid="travesia-empezar"
              onPress={onEmpezar}
            >
              <Trans>Start sailing</Trans>
            </Boton>
          </div>
        </Bocadillo>
      </div>
    </>,
    document.body,
  );
}
